/**
 * The gate for the host, create and admin screens. Not a permission check: the server's
 * `requireAccount` gates every route; this just shows a form instead of a broken screen.
 * A fresh install (`GET /api/auth/status`) defaults to register, since nobody can log in.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '../lib/http';
import { authApi, MIN_PASSWORD_LENGTH, type AccountSummary } from '../lib/auth';
import { noteSignedIn } from '../lib/whoami';

interface Props {
  title: string;
  children: ReactNode;
}

type Phase =
  /** Asking the server who we are. Renders nothing but a line of text. */
  | { kind: 'checking' }
  | { kind: 'form'; hasAccount: boolean; registrationOpen: boolean }
  | { kind: 'in'; account: AccountSummary };

export function AuthGate({ title, children }: Props) {
  const [phase, setPhase] = useState<Phase>({ kind: 'checking' });

  useEffect(() => {
    let live = true;
    Promise.all([authApi.me(), authApi.status()])
      .then(([me, status]) => {
        if (!live) return;
        // A login made elsewhere (another tab) that this page's store still calls signed out:
        // without this, `/signin` would bounce straight home with the plaque still saying "Sign in".
        if (me.account) noteSignedIn(me.account);
        setPhase(
          me.account
            ? { kind: 'in', account: me.account }
            : { kind: 'form', hasAccount: status.hasAccount, registrationOpen: status.registrationOpen },
        );
      })
      .catch(() => {
        // Unreachable, not signed out. Show the form; the submit surfaces the real error.
        if (live) setPhase({ kind: 'form', hasAccount: true, registrationOpen: true });
      });
    return () => {
      live = false;
    };
  }, []);

  if (phase.kind === 'checking') {
    return (
      <div className="centered">
        <p className="muted">Checking sign-in…</p>
      </div>
    );
  }

  if (phase.kind === 'in') return <>{children}</>;

  return (
    <SignInForm
      title={title}
      firstRun={!phase.hasAccount}
      registrationOpen={phase.registrationOpen}
      onSignedIn={(account) => setPhase({ kind: 'in', account })}
    />
  );
}

function SignInForm({
  title,
  firstRun,
  registrationOpen,
  onSignedIn,
}: {
  title: string;
  firstRun: boolean;
  registrationOpen: boolean;
  onSignedIn: (account: AccountSummary) => void;
}) {
  // `?signup` (the end-of-game nudge) opens on "create an account" rather than "sign in".
  const [searchParams] = useSearchParams();
  const [registering, setRegistering] = useState(
    firstRun || (registrationOpen && searchParams.has('signup')),
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = registering
        ? await authApi.register(email, password, displayName)
        : await authApi.login(email, password);
      // `<IdentityBar>` would otherwise say "Not signed in" until a reload.
      noteSignedIn(result.account);
      onSignedIn(result.account);
    } catch (err) {
      // Always the server's message; paraphrasing it here is how a form ends up wrong.
      setError(
        err instanceof ApiError ? err.message : 'Could not reach the server.',
      );
      setBusy(false);
    }
  }

  return (
    <div className="centered">
      <form className="card col" onSubmit={submit}>
        <h1>{title}</h1>
        {firstRun ? (
          <p className="muted" style={{ margin: 0 }}>
            First run on this server. Make your account to sign in.
          </p>
        ) : (
          <p className="muted" style={{ margin: 0 }}>
            {registering ? 'Make an account to host games and keep your scores.' : 'Sign in to your account.'}
          </p>
        )}

        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {registering ? (
          <div className="field">
            <label htmlFor="displayName">Your name</label>
            <input
              id="displayName"
              type="text"
              autoComplete="nickname"
              placeholder="optional"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete={registering ? 'new-password' : 'current-password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {registering ? (
            <span className="faint">At least {MIN_PASSWORD_LENGTH} characters.</span>
          ) : null}
        </div>

        {error ? <p className="error-text">{error}</p> : null}

        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? 'Working…' : registering ? 'Create account' : 'Sign in'}
        </button>

        {/* Not offered on a first run (nothing to sign in to) or when sign-up is closed. */}
        {firstRun || !registrationOpen ? null : (
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            onClick={() => {
              setRegistering((on) => !on);
              setError(null);
            }}
          >
            {registering ? 'I already have an account' : 'Create an account'}
          </button>
        )}
      </form>
    </div>
  );
}
